import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  ShieldOff,
  ShieldAlert,
  ShieldCheck,
  Search,
  Activity,
  AlertTriangle,
  ChevronRight,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { AdminLayout } from '../../components/Admin/AdminLayout';
import { CloakingThresholdsPanel } from '../../components/Admin/CloakingThresholdsPanel';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
const BASE_DOMAIN = import.meta.env.VITE_BASE_DOMAIN || 'hitzlink.com';

/**
 * Normalise whatever the admin pasted into the search box so a full
 * page URL like `https://shop-fbadsq14.hitzlink.com/` matches the
 * same row as just typing `shop-fbadsq14`. We strip the protocol,
 * any leading `www.`, the trailing slash and the platform's base
 * domain — leaving just the slug fragment we actually filter on.
 */
function normaliseSearchQuery(raw: string): string {
  let q = raw.trim().toLowerCase();
  if (!q) return '';
  q = q.replace(/^https?:\/\//, '');
  q = q.replace(/^www\./, '');
  q = q.replace(/\/+$/, '');
  // Drop the platform base domain so a pasted URL collapses to just
  // its subdomain. e.g. `shop-fbadsq14.hitzlink.com` -> `shop-fbadsq14`.
  const baseSuffix = `.${BASE_DOMAIN.toLowerCase()}`;
  if (q.endsWith(baseSuffix)) q = q.slice(0, -baseSuffix.length);
  return q;
}

type Range = 'today' | 'yesterday' | '7d' | '30d';
type Verdict = 'off' | 'idle' | 'normal' | 'elevated' | 'aggressive' | 'lenient';

interface OverviewRow {
  id: string;
  name: string;
  slug: string | null;
  userId: string;
  userName: string;
  userEmail: string;
  cloaking: {
    enabled: boolean;
    allowedCountries: string[];
    blockedCountries: string[];
    autoBlockDatacenters: boolean;
    adNiche: string | null;
    safePageTemplate: string | null;
    safePageUrl: string;
  };
  summary: {
    totalBlocks: number;
    views: number;
    totalHits: number;
    blockRate: number;
    falsePositiveCount: number;
    falsePositiveRate: number;
  };
  verdict: {
    status: Verdict;
    severity: number;
    notes: string[];
  };
  recommendationsCount?: number;
  topRecommendationSeverity?: 'critical' | 'high' | 'medium' | 'low' | null;
}

interface OverviewResponse {
  range: Range;
  startDate: string;
  endDate: string;
  pages: OverviewRow[];
}

export const AdminBotCloaking: React.FC = () => {
  const { t } = useTranslation('admin');
  const token = useAuthStore((s) => s.token);
  const navigate = useNavigate();
  const [range, setRange] = useState<Range>('today');
  const [search, setSearch] = useState('');
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchOverview = async (showSpinner = true) => {
    if (!token) return;
    if (showSpinner) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/admin/cloaking/overview?range=${range}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: OverviewResponse = await res.json();
      setData(json);
    } catch (err) {
      console.error('[AdminBotCloaking] fetch error:', err);
      toast.error(t('botCloaking.overview.loadFailed'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchOverview(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = normaliseSearchQuery(search);
    if (!q) return data.pages;
    return data.pages.filter((p) => {
      const slug = (p.slug || '').toLowerCase();
      const fullUrl = slug ? `${slug}.${BASE_DOMAIN.toLowerCase()}` : '';
      return (
        p.name.toLowerCase().includes(q) ||
        slug.includes(q) ||
        fullUrl.includes(q) ||
        p.id.toLowerCase().includes(q) ||
        p.userName.toLowerCase().includes(q) ||
        p.userEmail.toLowerCase().includes(q)
      );
    });
  }, [data, search]);

  const stats = useMemo(() => {
    if (!data) {
      return { enabled: 0, totalBlocks: 0, alerts: 0, falsePositives: 0, recommendations: 0 };
    }
    let enabled = 0;
    let totalBlocks = 0;
    let alerts = 0;
    let falsePositives = 0;
    let recommendations = 0;
    for (const p of data.pages) {
      if (p.cloaking.enabled) enabled++;
      totalBlocks += p.summary.totalBlocks;
      if (p.verdict.severity >= 2) alerts++;
      falsePositives += p.summary.falsePositiveCount;
      recommendations += p.recommendationsCount || 0;
    }
    return { enabled, totalBlocks, alerts, falsePositives, recommendations };
  }, [data]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Activity className="w-12 h-12 text-purple-600 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Shield className="w-8 h-8 text-orange-600" />
              {t('botCloaking.overview.title')}
            </h1>
            <p className="text-gray-600 mt-1 text-sm">
              {t('botCloaking.overview.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <RangePicker value={range} onChange={setRange} rangeLabel={(r) => t(`botCloaking.overview.range.${r}`)} />
            <button
              type="button"
              onClick={() => navigate('/admin/cloaking/recommendations')}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold relative"
            >
              <Sparkles className="w-4 h-4" />
              {t('botCloaking.overview.tuningAdvisor')}
              {stats.recommendations > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-white text-purple-700 text-xs font-bold tabular-nums">
                  {stats.recommendations}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => fetchOverview(false)}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-sm font-medium text-gray-700 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              {t('botCloaking.overview.refresh')}
            </button>
          </div>
        </div>

        {/* Global threshold editor — collapsible. Sits above the per-page
            table so admins can see the relationship between the verdict
            cut-offs they just changed and the rows that suddenly turn
            red/amber. */}
        <CloakingThresholdsPanel />

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            label={t('botCloaking.overview.stats.cloakingEnabled')}
            value={stats.enabled}
            sub={t('botCloaking.overview.stats.ofPages', { count: data?.pages.length || 0 })}
            icon={<ShieldCheck className="w-7 h-7 text-emerald-500" />}
            tone="emerald"
          />
          <StatCard
            label={t('botCloaking.overview.stats.blocks', {
              range: t(`botCloaking.overview.range.${range}`),
            })}
            value={stats.totalBlocks.toLocaleString()}
            sub={t('botCloaking.overview.stats.acrossPages')}
            icon={<Shield className="w-7 h-7 text-orange-500" />}
            tone="orange"
          />
          <StatCard
            label={t('botCloaking.overview.stats.needsAttention')}
            value={stats.alerts}
            sub={t('botCloaking.overview.stats.severityHint')}
            icon={<ShieldAlert className="w-7 h-7 text-rose-500" />}
            tone="rose"
          />
          <StatCard
            label={t('botCloaking.overview.stats.falsePositiveSamples')}
            value={stats.falsePositives}
            sub={t('botCloaking.overview.stats.trustedUaBlocked')}
            icon={<AlertTriangle className="w-7 h-7 text-amber-500" />}
            tone="amber"
          />
        </div>

        {/* Search */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('botCloaking.overview.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                    {t('botCloaking.overview.table.page')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                    {t('botCloaking.overview.table.owner')}
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">
                    {t('botCloaking.overview.table.cloaking')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">
                    {t('botCloaking.overview.table.blocks')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">
                    {t('botCloaking.overview.table.views')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">
                    {t('botCloaking.overview.table.blockRate')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">
                    {t('botCloaking.overview.table.fp')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                    {t('botCloaking.overview.table.verdict')}
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">
                    {t('botCloaking.overview.table.tips')}
                  </th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filtered.map((p) => (
                  <tr
                    key={p.id}
                    onClick={() => navigate(`/admin/cloaking/${p.id}`)}
                    className="hover:bg-orange-50/40 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{p.name}</div>
                      <div className="text-xs text-gray-500 truncate max-w-[180px]">
                        {p.slug || p.id.slice(0, 8)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900">{p.userName}</div>
                      <div className="text-xs text-gray-500 truncate max-w-[180px]">{p.userEmail}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {p.cloaking.enabled ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-xs font-semibold">
                          <ShieldCheck className="w-3 h-3" />
                          {t('botCloaking.overview.cloakingOn')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 text-xs font-semibold">
                          <ShieldOff className="w-3 h-3" />
                          {t('botCloaking.overview.cloakingOff')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                      {p.summary.totalBlocks.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-700">
                      {p.summary.views.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums">
                      <BlockRate rate={p.summary.blockRate} />
                    </td>
                    <td className="px-4 py-3 text-right text-sm">
                      {p.summary.falsePositiveCount > 0 ? (
                        <span className="inline-flex items-center gap-1 text-amber-700 font-medium">
                          <AlertTriangle className="w-3 h-3" />
                          {p.summary.falsePositiveCount}
                        </span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <VerdictBadge verdict={p.verdict.status} severity={p.verdict.severity} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <RecommendationBadge
                        count={p.recommendationsCount || 0}
                        severity={p.topRecommendationSeverity || null}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-4 py-12 text-center text-sm text-gray-500">
                      {t('botCloaking.overview.noResults')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

// ----------------- subcomponents -----------------

function StatCard({
  label,
  value,
  sub,
  icon,
  tone,
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ReactNode;
  tone: 'emerald' | 'orange' | 'rose' | 'amber';
}) {
  const toneBorder: Record<typeof tone, string> = {
    emerald: 'border-emerald-100 bg-emerald-50/40',
    orange: 'border-orange-100 bg-orange-50/40',
    rose: 'border-rose-100 bg-rose-50/40',
    amber: 'border-amber-100 bg-amber-50/40',
  };
  return (
    <div
      className={`bg-white border ${toneBorder[tone]} rounded-xl p-5 hover:shadow-md transition-shadow`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">{label}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
          {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
        </div>
        {icon}
      </div>
    </div>
  );
}

function RangePicker({
  value,
  onChange,
  rangeLabel,
}: {
  value: Range;
  onChange: (r: Range) => void;
  rangeLabel: (r: Range) => string;
}) {
  const opts: Range[] = ['today', 'yesterday', '7d', '30d'];
  return (
    <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1 text-sm">
      {opts.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
            value === o ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          {rangeLabel(o)}
        </button>
      ))}
    </div>
  );
}

function RecommendationBadge({
  count,
  severity,
}: {
  count: number;
  severity: 'critical' | 'high' | 'medium' | 'low' | null;
}) {
  if (count === 0) {
    return <span className="text-gray-300 text-xs">—</span>;
  }
  const cls: Record<NonNullable<typeof severity>, string> = {
    critical: 'bg-rose-100 text-rose-700 border-rose-200',
    high: 'bg-orange-100 text-orange-700 border-orange-200',
    medium: 'bg-amber-100 text-amber-700 border-amber-200',
    low: 'bg-purple-50 text-purple-700 border-purple-200',
  };
  const tone = severity ? cls[severity] : cls.low;
  return (
    <span
      className={`inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 rounded-md border text-xs font-bold tabular-nums ${tone}`}
      title={`${count} tuning suggestion${count === 1 ? '' : 's'}`}
    >
      <Sparkles className="w-3 h-3 mr-0.5" /> {count}
    </span>
  );
}

function BlockRate({ rate }: { rate: number }) {
  const pct = (rate * 100).toFixed(1);
  let color = 'text-gray-700';
  if (rate >= 0.5) color = 'text-rose-600 font-semibold';
  else if (rate >= 0.3) color = 'text-orange-600 font-semibold';
  else if (rate >= 0.05) color = 'text-gray-700';
  else color = 'text-gray-400';
  return <span className={color}>{pct}%</span>;
}

export function VerdictBadge({
  verdict,
  severity,
}: {
  verdict: Verdict;
  severity: number;
}) {
  const { t } = useTranslation('admin');
  const cfg: Record<
    Verdict,
    { label: string; cls: string; icon: React.ReactNode }
  > = {
    off: {
      label: t('botCloaking.overview.verdict.off'),
      cls: 'bg-gray-100 text-gray-700 border-gray-200',
      icon: <ShieldOff className="w-3 h-3" />,
    },
    idle: {
      label: t('botCloaking.overview.verdict.idle'),
      cls: 'bg-slate-100 text-slate-600 border-slate-200',
      icon: <Shield className="w-3 h-3" />,
    },
    normal: {
      label: t('botCloaking.overview.verdict.normal'),
      cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      icon: <ShieldCheck className="w-3 h-3" />,
    },
    elevated: {
      label: t('botCloaking.overview.verdict.elevated'),
      cls: 'bg-amber-50 text-amber-700 border-amber-200',
      icon: <AlertTriangle className="w-3 h-3" />,
    },
    aggressive: {
      label:
        severity >= 3
          ? t('botCloaking.overview.verdict.critical')
          : t('botCloaking.overview.verdict.aggressive'),
      cls: 'bg-rose-50 text-rose-700 border-rose-200',
      icon: <ShieldAlert className="w-3 h-3" />,
    },
    lenient: {
      label: t('botCloaking.overview.verdict.lenient'),
      cls: 'bg-sky-50 text-sky-700 border-sky-200',
      icon: <Shield className="w-3 h-3" />,
    },
  };
  const c = cfg[verdict] || cfg.normal;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-semibold ${c.cls}`}
    >
      {c.icon}
      {c.label}
    </span>
  );
}
